import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { Heading2 } from '../components/UI/Typography';
import Input from '../components/UI/Input';
import Button from '../components/UI/Button';
import { useGetUserByIdQuery, useUpdateUserMutation } from '../api/userAPI';
import { useParams } from 'react-router-dom';

const schema = z.object({
  name: z.string().min(2, 'נא להזין שם מלא'),
  email: z.string().email('כתובת מייל לא תקינה'),
  phone: z
    .string()
    .regex(/^0\d{9}$/, 'מספר טלפון חייב להכיל 10 ספרות')
    .or(z.literal('').transform(() => '')),
  address: z.string().optional(),
  notifications: z.boolean().default(false)
});
interface UserForm {
  name: string;
  email: string;
  phone: string;
  address?: string;
  notifications: boolean;
}
interface Errors {
  [key: string]: string;
}

const UserProfile = () => {
  const { id } = useParams();
  const { data: user, isLoading, error, refetch } = useGetUserByIdQuery(id!);
  const [updateUser] = useUpdateUserMutation();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<UserForm>({
    name: '',
    email: '',
    phone: '',
    address: '',
    notifications: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        notifications: user.notifications || false,
      });
      setErrors({});
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };



  const handleEdit = () => setEditMode(true);

  const handleCancel = () => {
    setEditMode(false);
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        notifications: user.notifications || false,
      });
      setErrors({});
    }
  };

  const handleSave = async () => {
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Errors = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSaving(true);
    try {
      await updateUser({
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        notifications: form.notifications,
      }).unwrap();
      setEditMode(false);
      setIsError(false);
      setSuccessMessage('הפרטים עודכנו בהצלחה!');
      setTimeout(() => setSuccessMessage(''), 4000);
      refetch();
    } catch (e: any) {
      setIsError(true);
      const msg = e?.data?.message || 'אירעה שגיאה בשמירת השינויים';
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(''), 4000);
    }
    setSaving(false);
  };

  if (isLoading) return <Loading />;
  if (error || !user) return <ErrorMessage message="שגיאה בטעינת המשתמש" />;

  return (
    <div dir="rtl" className="flex justify-center items-center min-h-screen bg-gradient-to-br from-[#eaf8ec] to-[#f6f8f7]">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-card p-0">
        <form className="flex flex-col items-center px-8 pb-8">
          <Heading2 className="text-primary font-bold text-3xl mt-10 mb-4">פרופיל משתמש</Heading2>

          <ProfileRow
            label="שם"
            value={
              editMode ? (
                <ProfileInput name="name" value={form.name} onChange={handleChange} error={errors.name} />
              ) : (
                <span className="text-base text-gray-900">{user.name}</span>
              )
            }
          />
          <ProfileRow
            label="אימייל"
            value={
              editMode ? (
                <ProfileInput
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  error={errors.email}
                />
              ) : (
                <span className="text-base text-gray-900">{user.email}</span>
              )
            }
          />
          <ProfileRow
            label="טלפון"
            value={
              editMode ? (
                <ProfileInput name="phone" value={form.phone} onChange={handleChange} error={errors.phone} />
              ) : (
                <span className="text-base text-gray-900">{user.phone}</span>
              )
            }
          />
          <ProfileRow
            label="כתובת"
            value={
              editMode ? (
                <ProfileInput name="address" value={form.address || ''} onChange={handleChange} error={errors.address} />
              ) : (
                <span className="text-base text-gray-900">{user.address || '-'}</span>
              )
            }
          />
          <ProfileRow
            label="הגדרות התראות"
            value={
              editMode ? (
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="email"
                      checked={form.notifications === false}
                      onChange={() =>
                        setForm({ ...form, notifications: false })
                      }
                      disabled={saving}
                    />
                    מייל
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="inApp"
                      checked={form.notifications === true}
                      onChange={() =>
                        setForm({ ...form, notifications: true })
                      }
                      disabled={saving}
                    />
                    מערכת
                  </label>
                </div>
              ) : (
                <span className="text-base text-gray-900">
                  {form.notifications === true && 'מערכת'}
                  {form.notifications === false && 'מייל'}
                </span>
              )
            }
          />
          <ProfileRow
            label="התחברות אחרונה"
            value={
              <span className="text-base text-gray-900">
                {user.last_login
                  ? new Date(user.last_login).toLocaleString('he-IL', {
                    hour: '2-digit',
                    minute: '2-digit',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })
                  : '-'}
              </span>
            }
          />
          <ProfileRow label="תפקיד" value={<span className="text-base text-gray-700">{user.role}</span>} />

          <div className="w-full flex justify-end gap-2 mt-10">
            {editMode ? (
              <>
                <Button
                  onClick={handleSave}
                  variant="primary-dark"
                  type="button"
                  className="w-full rounded-full py-3 text-lg font-bold"
                  isLoading={saving}
                  size="lg"
                  disabled={saving}
                >
                  שמירת שינויים
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  type="button"
                  className="w-full rounded-full py-3 text-lg font-bold"
                  disabled={saving}
                  size="lg"
                >
                  ביטול
                </Button>
              </>
            ) : (
              <Button
                onClick={handleEdit}
                variant="primary-dark"
                type="button"
                className="w-full rounded-full py-3 text-lg font-bold"
                size="lg"
              >
                עריכת פרופיל
              </Button>
            )}
          </div>
          {successMessage && (
            <p className={`text-lg font-semibold mt-6 text-center ${isError ? 'text-danger' : 'text-success'}`}>
              {successMessage}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

const ProfileInput = ({
  name,
  value,
  onChange,
  error,
  type = 'text',
}: {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: string;
}) => (
  <div className="flex flex-col w-full max-w-xs">
    <Input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="py-2 px-3 border rounded-md"
    />
    {error && <p className="text-danger mt-1 text-sm">{error}</p>}
  </div>
);

const ProfileRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-row justify-between items-center w-full mt-3">
    <p className="text-lg font-semibold text-gray-700">{label}</p>
    <div>{value}</div>
  </div>
);

const Loading = () => <p>טוען...</p>;
const ErrorMessage = ({ message }: { message: string }) => (
  <p className="text-danger">{message}</p>
);

export default UserProfile;
